import * as React from 'react';
import Typography from '@mui/material/Typography';
import { Box, Container, Card, CardContent, Grid, Chip, Divider } from '@mui/material';
import Business from '@mui/icons-material/Business';
import Email from '@mui/icons-material/Email';
import Badge from '@mui/icons-material/Badge';
import AttachMoney from '@mui/icons-material/AttachMoney';
import TrendingUp from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import { url } from '../../utils/constants';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function EmpresaList({ usePageTitle }) {
    usePageTitle?.('PixTick | Empresas');
    const [empresas, setEmpresas] = React.useState([]);
    const [taxasPorEmail, setTaxasPorEmail] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const navigate = useNavigate();
    const { logout } = useAuth();

    React.useEffect(() => {
        async function fetchData() {
            try {
                setError(null);
                const token = localStorage.getItem('token');
                const { data } = await axios.get(url + '/api/v1/user/empresas', {
                    headers: { Authorization: 'Bearer ' + token }
                });
                const list = Array.isArray(data?.data) ? data.data : [];
                setEmpresas(list);

                if (list.length > 0) {
                    const headers = { Authorization: 'Bearer ' + token };
                    const results = await Promise.all(
                        list.map(async (emp) => {
                            try {
                                const res = await axios.get(url + '/api/v1/fee-settings', {
                                    headers,
                                    params: { targetEmail: emp.email }
                                });
                                return { email: emp.email, taxas: res.data?.data || null };
                            } catch {
                                return { email: emp.email, taxas: null };
                            }
                        })
                    );
                    const map = {};
                    results.forEach((r) => { map[r.email] = r.taxas; });
                    setTaxasPorEmail(map);
                }
            } catch (err) {
                if (err.response?.status === 401) {
                    logout();
                    navigate('/signin');
                } else {
                    setError('Erro ao carregar empresas.');
                }
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [navigate, logout]);

    const renderTaxas = (taxas) => {
        if (!taxas) return <Typography variant="caption" color="text.secondary">Taxas não carregadas</Typography>;
        const fee = taxas.serviceFee ?? 0;
        const ativa = taxas.serviceFeeActive !== false;
        const cambios = taxas.exchangeRates || {};
        return (
            <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <AttachMoney sx={{ fontSize: 14 }} />
                    <Typography variant="body2">
                        Taxa: <strong>{fee}%</strong> {ativa ? '(ativa)' : '(inativa)'}
                    </Typography>
                </Box>
                {cambios.ARS_BRL && (
                    <Typography variant="caption" display="block" color="text.secondary">
                        ARS→BRL: {cambios.ARS_BRL.rate} {cambios.ARS_BRL.isActive ? '' : '(inativo)'}
                    </Typography>
                )}
                {cambios.BRL_ARS && (
                    <Typography variant="caption" display="block" color="text.secondary">
                        BRL→ARS: {cambios.BRL_ARS.rate} {cambios.BRL_ARS.isActive ? '' : '(inativo)'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Container sx={{ marginTop: '80px', pb: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business /> Empresas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Empresas e configurações de taxas. Para alterar, use o menu &quot;Configurações de Taxas&quot; e selecione a empresa.
                </Typography>
            </Box>

            {loading ? (
                <Typography variant="body1" color="text.secondary">Carregando empresas e taxas...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : empresas.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Business sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">Nenhuma empresa cadastrada</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        As empresas aparecem aqui quando existir usuário com nível Administrador.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {empresas.map((emp) => (
                        <Grid item xs={12} sm={6} md={4} key={emp._id || emp.email}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {emp.empresa || emp.fullname || '—'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Badge fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            CNPJ: {emp.cnpj || '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Email fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            {emp.email}
                                        </Typography>
                                    </Box>
                                    <Chip label="Administrador" size="small" color="primary" sx={{ mt: 1 }} />
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <TrendingUp fontSize="small" color="action" />
                                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                            Configurações de taxas
                                        </Typography>
                                    </Box>
                                    {renderTaxas(taxasPorEmail[emp.email])}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
}
